/**
 * Demo Hubwayz-shaped fixtures for hotels-only search mockup.
 * Prices / combination shapes mirror Phase 2U live samples — no invented live API calls.
 */

import { mapHubwayzToFlightHotelCards } from "../unifiedSearchMockup/mapHubwayzToFlightHotelCards";
import {
  UNIFIED_DESTINATIONS,
  cloneSearch,
  createInitialDraftSearch,
  summarizeOccupancy,
} from "../unifiedSearchMockup/demoFixtures";

export { UNIFIED_DESTINATIONS, cloneSearch, summarizeOccupancy };

const DEMO_IMAGE =
  "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1200&q=80&auto=format&fit=crop";

/** Athens-style demo inventory (1-room + 3-room complete combinations). */
export const DEMO_HUBWAYZ_DATA = {
  hotelsById: {
    "91001": {
      name: "Lacoba Hotel",
      stars: 2,
      city: "Athens",
      country: "Greece",
      address: "Tagmatarchou Plessa 19-25, Athens, 17674",
      mainImage: null,
      images: [{ url: DEMO_IMAGE }],
      description:
        "מלון עירוני קומפקטי במרכז אתונה — דמו לפי מדגם Pre-Book חי (Phase 2U).",
    },
    "91002": {
      name: "Asomaton",
      stars: 3,
      city: "Athens",
      country: "Greece",
      address: "Asomaton district, Athens",
      images: [
        {
          url: "https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=1200&q=80&auto=format&fit=crop",
        },
      ],
      description: "מלון בוטיק — הצעות חדר בודד ושילובי 3 חדרים.",
    },
    "91003": {
      name: "Acropolis View Inn",
      stars: 4,
      city: "Athens",
      country: "Greece",
      address: "Plaka, Athens",
      images: [
        {
          url: "https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=1200&q=80&auto=format&fit=crop",
        },
      ],
      description: "נוף לאקרופוליס · שילובי חדרים מלאים מספק.",
    },
  },
  results: [
    {
      code: "DEMO-1R-LACOBA-RO",
      confirmation: "immediate",
      commissionable: true,
      netPrice: { amount: 64.2, currency: "USD" },
      price: { amount: 72, currency: "USD" },
      cancellation: { type: "fully-refundable" },
      items: [
        {
          hotelId: "91001",
          name: "Dormitory Single",
          board: "RO",
          category: "Dormitory",
          bedding: "1 bed",
          pax: { adults: 1, children: [] },
        },
      ],
    },
    {
      code: "DEMO-1R-ASOMATON-BB",
      confirmation: "immediate",
      commissionable: true,
      netPrice: { amount: 118, currency: "USD" },
      price: { amount: 130, currency: "USD" },
      cancellation: { type: "fully-refundable" },
      items: [
        {
          hotelId: "91002",
          name: "Standard Double",
          board: "BB",
          category: "Double",
          bedding: "Double bed",
          pax: { adults: 2, children: [] },
        },
      ],
    },
    {
      code: "DEMO-1R-ACRO-HB",
      confirmation: "onRequest",
      commissionable: true,
      netPrice: { amount: 245, currency: "USD" },
      price: { amount: 268, currency: "USD" },
      cancellation: { type: "non-refundable" },
      items: [
        {
          hotelId: "91003",
          name: "Superior City View",
          board: "HB",
          category: "Superior",
          bedding: "King",
          pax: { adults: 2, children: [] },
        },
      ],
    },
    {
      code: "DEMO-3R-LACOBA-MIX",
      confirmation: "immediate",
      commissionable: true,
      netPrice: { amount: 17.55, currency: "USD" },
      price: { amount: 19.5, currency: "USD" },
      cancellation: { type: "fully-refundable" },
      items: [
        {
          hotelId: "91001",
          name: "1 beds in shared room for 3 person",
          board: "RO",
          category: "Shared",
          bedding: "1 bed",
          pax: { adults: 1, children: [] },
        },
        {
          hotelId: "91001",
          name: "1 beds in shared room for 1 person",
          board: "RO",
          category: "Shared",
          bedding: "1 bed",
          pax: { adults: 1, children: [] },
        },
        {
          hotelId: "91001",
          name: "1 beds in shared room for 4 person",
          board: "RO",
          category: "Shared",
          bedding: "1 bed",
          pax: { adults: 1, children: [] },
        },
      ],
    },
    {
      code: "DEMO-3R-ASOMATON",
      confirmation: "immediate",
      commissionable: true,
      netPrice: { amount: 210, currency: "USD" },
      price: { amount: 228, currency: "USD" },
      cancellation: { type: "partially-refundable" },
      items: [
        {
          hotelId: "91002",
          name: "Twin Room",
          board: "BB",
          pax: { adults: 1, children: [] },
        },
        {
          hotelId: "91002",
          name: "Single Room",
          board: "BB",
          pax: { adults: 1, children: [] },
        },
        {
          hotelId: "91002",
          name: "Economy Single",
          board: "RO",
          pax: { adults: 1, children: [] },
        },
      ],
    },
    {
      code: "DEMO-3R-ACRO",
      confirmation: "onRequest",
      commissionable: true,
      netPrice: { amount: 390, currency: "USD" },
      price: { amount: 420, currency: "USD" },
      cancellation: { type: "non-refundable" },
      items: [
        {
          hotelId: "91003",
          name: "Deluxe",
          board: "HB",
          pax: { adults: 1, children: [] },
        },
        {
          hotelId: "91003",
          name: "Deluxe",
          board: "HB",
          pax: { adults: 1, children: [] },
        },
        {
          hotelId: "91003",
          name: "Standard",
          board: "BB",
          pax: { adults: 1, children: [] },
        },
      ],
    },
  ],
};

export function createHotelsOnlyDraftSearch() {
  const dest = UNIFIED_DESTINATIONS.find((d) => d.id === "athens") || UNIFIED_DESTINATIONS[0];
  const base = createInitialDraftSearch();
  return {
    ...base,
    origin: "",
    originLabel: "",
    destinationId: dest.id,
    destinationLabel: dest.label,
    packageDestinationId: dest.packageDestinationId,
    flightIata: dest.flightIata,
    checkIn: "2027-03-20",
    checkOut: "2027-03-23",
    dateRangeLabel: "2027-03-20 → 2027-03-23",
    rooms: [{ adults: 1, childAges: [] }],
  };
}

function nightsBetween(checkIn, checkOut) {
  try {
    const a = new Date(`${checkIn}T12:00:00`);
    const b = new Date(`${checkOut}T12:00:00`);
    const d = Math.round((b - a) / 86400000);
    return d > 0 ? d : null;
  } catch {
    return null;
  }
}

/**
 * Filter demo results so multi-room searches only surface complete combinations.
 */
export function filterDemoHubwayzByRoomCount(hubwayzData, roomCount) {
  const need = Math.max(1, Number(roomCount) || 1);
  const results = (hubwayzData?.results || []).filter((offer) => {
    const items = Array.isArray(offer?.items) ? offer.items : [];
    if (need === 1) return items.length === 1;
    return items.length === need;
  });
  return {
    hotelsById: hubwayzData?.hotelsById || {},
    results,
  };
}

export function matchDemoHotelsSearch(appliedSearch) {
  const rooms = Array.isArray(appliedSearch?.rooms) ? appliedSearch.rooms : [{ adults: 2 }];
  const roomCount = Math.max(1, rooms.length);
  const occ = summarizeOccupancy(rooms);
  const nights = nightsBetween(appliedSearch.checkIn, appliedSearch.checkOut);
  const dateRangeLabel =
    appliedSearch.dateRangeLabel ||
    `${appliedSearch.checkIn || ""} → ${appliedSearch.checkOut || ""}`;
  const dest = UNIFIED_DESTINATIONS.find((d) => d.id === appliedSearch.destinationId);
  const filtered = filterDemoHubwayzByRoomCount(DEMO_HUBWAYZ_DATA, roomCount);
  const cards = mapHubwayzToFlightHotelCards(filtered, {
    destinationId: appliedSearch.destinationId,
    dateRangeLabel,
    nights,
    cityLabel: dest?.label || appliedSearch.destinationLabel,
    countryLabel: dest?.countryLabel || "",
    searchRequest: {
      demo: true,
      destinationId: appliedSearch.destinationId,
      checkIn: appliedSearch.checkIn,
      checkOut: appliedSearch.checkOut,
      rooms,
    },
    paxCount: Math.max(1, occ.adults + occ.children),
  }).map((c) => ({
    ...c,
    hotelsOnly: true,
    productLabel: "מלון",
    flightOptionsCount: 0,
    outbound: null,
    inbound: null,
  }));

  return {
    cards,
    hubwayzData: filtered,
    searchRequest: {
      demo: true,
      checkIn: appliedSearch.checkIn,
      checkOut: appliedSearch.checkOut,
      rooms,
    },
    matched: cards.length > 0,
    meta: {
      nights,
      dateRangeLabel,
      hotelsCount: cards.length,
      roomCount,
      occupancyLabel: occ.occupancyLabel,
    },
  };
}
